import React, { useRef, useState } from 'react';
import { C, F, T, Btn, Pill, Card } from '../theme/uiKit';
import { getUploadService } from '../services';

// Supplier Gateway · ASN (Advance Shipping Notice). Summary/worklist render
// empty/zero states until the backend is wired; the upload uses the real
// upload service.

type Preset = '24h' | '7d' | '30d';
type Integration = 'API' | 'File Drop' | 'Presigned URL';

// Selected date range: one of the presets, or a custom From/To window.
type ActiveRange =
    | { kind: 'preset'; preset: Preset }
    | { kind: 'custom'; from: string; to: string; days: number };

const INTEGRATIONS: Integration[] = ['API', 'File Drop', 'Presigned URL'];
const PRESETS: Preset[] = ['24h', '7d', '30d'];

const TAG_BY_INTEGRATION: Record<Integration, string> = {
    API: 'REST',
    'File Drop': 'CSV or Excel',
    'Presigned URL': 'S3',
};

const rangeLabel = (r: ActiveRange): string =>
    r.kind === 'preset' ? r.preset : `${r.from} → ${r.to}`;

// Inclusive day count between two yyyy-mm-dd dates; 0 if invalid/reversed.
function daysBetween(from: string, to: string): number {
    if (!from || !to) return 0;
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
}

const PENDING_HEADERS = [
    'Line #',
    'PO',
    'Item',
    'Description',
    'Ordered',
    'Received',
    'Outstanding',
    'Need-by date',
    'Status',
];

// "Coming up" horizon options for the Pending ASNs worklist.
const PENDING_HORIZONS = [7, 14, 30, 'all'] as const;
type Horizon = (typeof PENDING_HORIZONS)[number];
const horizonLabel = (h: Horizon) => (h === 'all' ? '30+' : `${h}d`);

// CSV export columns for the pending worklist.
const PENDING_EXPORT_HEADERS = [
    'Line #',
    'PO',
    'Item',
    'Description',
    'Ordered',
    'Received',
    'Outstanding',
    'Need by date',
    'Bucket',
];

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ── Per-integration summary card (counts empty until backend is wired) ────────
type Bucket = 'data' | 'business';

function FailCount({
    label,
    count,
    color,
    onClick,
}: {
    label: string;
    count: number;
    color: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={count === 0}
            style={{
                flex: 1,
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: count ? 'pointer' : 'default',
                fontFamily: F.ui,
            }}
        >
            <div style={{ fontSize: T.caption, color: C.textMuted, marginBottom: 2 }}>{label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: T.section, fontWeight: 700, color: count ? color : C.textMuted }}>
                    {count}
                </span>
                {count > 0 && <span style={{ fontSize: T.caption, color: C.primary }}>view →</span>}
            </div>
        </button>
    );
}

function IntegrationCard({
    intg,
    onViewFailures,
}: {
    intg: Integration;
    onViewFailures: (bucket: Bucket) => void;
}) {
    // Counts are zero until the backend is wired.
    const total = 0;
    const dataErrors = 0;
    const businessErrors = 0;
    return (
        <Card>
            <div
                style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${C.borderLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <span style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>{intg}</span>
                <Pill label={TAG_BY_INTEGRATION[intg]} color="blue" />
            </div>
            <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: T.caption, color: C.textMuted, marginBottom: 2 }}>
                    Total ASNs
                </div>
                <div style={{ fontSize: T.display, fontWeight: 700, color: C.text, marginBottom: 14 }}>
                    {total}
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: 16,
                        borderTop: `1px solid ${C.borderLight}`,
                        paddingTop: 12,
                    }}
                >
                    <FailCount
                        label="Data errors"
                        count={dataErrors}
                        color={C.amber}
                        onClick={() => onViewFailures('data')}
                    />
                    <FailCount
                        label="Business errors"
                        count={businessErrors}
                        color={C.red}
                        onClick={() => onViewFailures('business')}
                    />
                </div>
            </div>
        </Card>
    );
}

// ── Pending ASNs (empty until backend is wired) ───────────────────────────────

// One titled section (Overdue / Coming up) with its own header + empty table.
function PendingSection({
    title,
    tone,
    note,
    count,
    emptyText,
}: {
    title: string;
    tone: 'red' | 'neutral';
    note: string;
    count: number;
    emptyText: string;
}) {
    const headColor = tone === 'red' ? C.red : C.text;
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    padding: '12px 16px',
                    background: tone === 'red' ? C.redSoft : C.bg,
                    borderBottom: `1px solid ${C.borderLight}`,
                }}
            >
                <span style={{ fontSize: T.body, fontWeight: 700, color: headColor }}>{title}</span>
                <span style={{ fontSize: T.caption, color: C.textMuted }}>{note}</span>
                <span
                    style={{
                        marginLeft: 'auto',
                        fontSize: T.caption,
                        fontWeight: 700,
                        color: headColor,
                    }}
                >
                    {count}
                </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                <thead>
                    <tr style={{ textAlign: 'left', color: C.textSub }}>
                        {PENDING_HEADERS.map((h) => (
                            <th
                                key={h}
                                style={{
                                    padding: '8px 14px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    fontSize: T.caption,
                                }}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr style={{ borderTop: `1px solid ${C.borderLight}` }}>
                        <td
                            colSpan={PENDING_HEADERS.length}
                            style={{ padding: 24, textAlign: 'center', color: C.textMuted }}
                        >
                            {emptyText}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function PendingAsns() {
    const [horizon, setHorizon] = useState<Horizon>(14);

    // No backend yet, so both sections are empty. Export still produces a valid
    // (header-only) CSV so the flow works end-to-end.
    const exportCsv = () => downloadCsv('pending-asns.csv', `${PENDING_EXPORT_HEADERS.join(',')}\n`);

    return (
        <Card style={{ marginBottom: 24 }}>
            <div
                style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${C.borderLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <div>
                    <div style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>
                        Pending ASNs
                    </div>
                    <div style={{ fontSize: T.caption, color: C.textMuted }}>
                        What still needs an ASN: everything overdue, plus what&rsquo;s coming up.
                        Received = units you&rsquo;ve already notified.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Horizon selector for the "Coming up" window */}
                    <span style={{ fontSize: T.caption, color: C.textSub }}>Coming up:</span>
                    <div
                        style={{
                            display: 'flex',
                            gap: 4,
                            background: C.bg,
                            border: `1px solid ${C.border}`,
                            borderRadius: 8,
                            padding: 3,
                        }}
                    >
                        {PENDING_HORIZONS.map((h) => (
                            <button
                                key={String(h)}
                                type="button"
                                onClick={() => setHorizon(h)}
                                style={{
                                    padding: '5px 12px',
                                    borderRadius: 6,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: F.ui,
                                    fontSize: T.caption,
                                    fontWeight: horizon === h ? 700 : 500,
                                    background: horizon === h ? C.primary : 'transparent',
                                    color: horizon === h ? '#fff' : C.textSub,
                                }}
                            >
                                {horizonLabel(h)}
                            </button>
                        ))}
                    </div>
                    <Btn label="⤓ Export" variant="sec" size="xs" onClick={exportCsv} />
                </div>
            </div>

            <PendingSection
                title={horizon === 'all' ? 'Overdue 30+ days' : 'Overdue'}
                tone="red"
                note={
                    horizon === 'all'
                        ? 'overdue by more than 30 days — escalate'
                        : 'past need-by — ship and notify now'
                }
                count={0}
                emptyText={
                    horizon === 'all'
                        ? 'Nothing overdue by more than 30 days.'
                        : 'Nothing overdue.'
                }
            />
            <PendingSection
                title={horizon === 'all' ? 'Due beyond 30 days' : 'Coming up'}
                tone="neutral"
                note={
                    horizon === 'all'
                        ? 'need-by more than 30 days out'
                        : `due within the next ${horizon} days`
                }
                count={0}
                emptyText={
                    horizon === 'all'
                        ? 'Nothing due beyond 30 days.'
                        : `Nothing due in the next ${horizon} days.`
                }
            />
        </Card>
    );
}

// ── Upload ASN file (wired to the real upload service) ────────────────────────
function UploadAsn() {
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const onPick = (f: File | undefined) => {
        if (f) {
            setFile(f);
            setFileName(f.name);
            setSubmitted(false);
            setStatus(null);
            setError(null);
        }
    };

    const reset = () => {
        setFile(null);
        setFileName(null);
        setSubmitted(false);
        setStatus(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const submit = async () => {
        if (!file) return;
        setError(null);
        setStatus(null);
        setProgress(0);
        try {
            await getUploadService().uploadFile(file, setProgress);
            setSubmitted(true);
            setStatus(`Uploaded ${file.name} successfully.`);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Upload failed.');
        } finally {
            setProgress(null);
        }
    };

    if (submitted) {
        return (
            <Card style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>OK</div>
                <h2 style={{ margin: 0, fontSize: T.section, fontWeight: 700, color: C.text }}>
                    ASN submitted
                </h2>
                <p style={{ margin: '8px 0 16px', fontSize: T.body, color: C.textSub }}>
                    {status}
                </p>
                <Btn label="Upload another file" variant="sec" onClick={reset} />
            </Card>
        );
    }

    return (
        <>
            <label
                htmlFor="asn-file-input"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    onPick(e.dataTransfer.files?.[0]);
                }}
                style={{
                    display: 'block',
                    border: `2px dashed ${C.border}`,
                    borderRadius: 12,
                    padding: '40px 24px',
                    textAlign: 'center',
                    background: C.surface,
                    cursor: 'pointer',
                }}
            >
                <div style={{ fontSize: 34, marginBottom: 10 }}>⬆</div>
                <div style={{ fontSize: T.section, fontWeight: 600, color: C.text }}>
                    {fileName ? 'Choose a different file' : 'Drop your ASN file here or browse'}
                </div>
                <div style={{ fontSize: T.body, color: C.textMuted, marginTop: 4 }}>
                    CSV or Excel (.csv, .xlsx) · up to 25 MB
                </div>
                <input
                    id="asn-file-input"
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }}
                    onChange={(e) => onPick(e.target.files?.[0])}
                />
            </label>

            {fileName && (
                <Card style={{ marginTop: 16 }}>
                    <div
                        style={{
                            padding: '11px 16px',
                            borderBottom: `1px solid ${C.borderLight}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>
                            {fileName}
                        </span>
                        <Pill label="Ready to submit" color="blue" dot />
                    </div>
                    {progress !== null && (
                        <div style={{ padding: '12px 16px' }}>
                            <div
                                style={{
                                    height: 7,
                                    background: C.graySoft,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        width: `${progress}%`,
                                        height: '100%',
                                        background: C.primary,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {error && (
                        <div style={{ padding: '12px 16px', color: C.red, fontSize: T.body }}>
                            {error}
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            padding: 14,
                            borderTop: `1px solid ${C.borderLight}`,
                        }}
                    >
                        <Btn label="Remove file" variant="ghost" onClick={reset} />
                        <Btn
                            label="Submit ASN"
                            variant="pri"
                            onClick={submit}
                            disabled={progress !== null}
                        />
                    </div>
                </Card>
            )}
        </>
    );
}

// ── Failed transactions drawer ────────────────────────────────────────────────
function FilterChip({
    label,
    active,
    onClick,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: T.caption,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: F.ui,
                background: active ? C.primary : C.surface,
                color: active ? '#fff' : C.textSub,
                border: `1px solid ${active ? C.primary : C.border}`,
            }}
        >
            {label}
        </button>
    );
}

const FAILURE_HEADERS = ['Transaction', 'PO', 'Line #', 'Integration', 'Error'];

/**
 * Slide-over panel listing failed transactions for the selected range. The
 * failure list is empty until the backend is wired; the filters and per-
 * integration tiles render with zero counts.
 */
function FailuresPanel({
    range,
    initIntegration,
    initBucket,
    onClose,
}: {
    range: ActiveRange;
    initIntegration: Integration | 'all';
    initBucket: Bucket | 'all';
    onClose: () => void;
}) {
    const [q, setQ] = useState('');
    const [integration, setIntegration] = useState<Integration | 'all'>(initIntegration);
    const [bucket, setBucket] = useState<Bucket | 'all'>(initBucket);

    // Close on Escape while the drawer is open.
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 20 }}>
            <button
                type="button"
                aria-label="Close failed transactions"
                onClick={onClose}
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    border: 'none',
                    padding: 0,
                    cursor: 'default',
                }}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Failed transactions"
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 700,
                    maxWidth: '100%',
                    background: C.surface,
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: F.ui,
                }}
            >
                <div
                    style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${C.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <div style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>
                            Failed transactions
                        </div>
                        <div style={{ fontSize: T.caption, color: C.textMuted }}>
                            Range: {rangeLabel(range)} · Data errors = what you sent, Business
                            errors = order match
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: 22,
                            cursor: 'pointer',
                            color: C.textMuted,
                        }}
                    >
                        ×
                    </button>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        padding: '12px 20px',
                        borderBottom: `1px solid ${C.borderLight}`,
                    }}
                >
                    {INTEGRATIONS.map((i) => (
                        <div
                            key={i}
                            style={{ flex: 1, background: C.bg, borderRadius: 8, padding: '10px 12px' }}
                        >
                            <div style={{ fontSize: T.caption, color: C.textMuted }}>{i}</div>
                            <div style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>
                                0{' '}
                                <span
                                    style={{
                                        fontSize: T.caption,
                                        fontWeight: 500,
                                        color: C.textMuted,
                                    }}
                                >
                                    failed
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    style={{
                        padding: '12px 20px',
                        borderBottom: `1px solid ${C.borderLight}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                    }}
                >
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by ASN, PO, or line #…"
                        style={{
                            width: '100%',
                            height: 36,
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            padding: '0 12px',
                            fontSize: T.body,
                            fontFamily: F.ui,
                            boxSizing: 'border-box',
                        }}
                    />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <FilterChip
                            label="All types"
                            active={bucket === 'all'}
                            onClick={() => setBucket('all')}
                        />
                        <FilterChip
                            label="Data errors"
                            active={bucket === 'data'}
                            onClick={() => setBucket('data')}
                        />
                        <FilterChip
                            label="Business errors"
                            active={bucket === 'business'}
                            onClick={() => setBucket('business')}
                        />
                        <span style={{ width: 1, background: C.border, margin: '0 2px' }} />
                        <FilterChip
                            label="All integrations"
                            active={integration === 'all'}
                            onClick={() => setIntegration('all')}
                        />
                        {INTEGRATIONS.map((i) => (
                            <FilterChip
                                key={i}
                                label={i}
                                active={integration === i}
                                onClick={() => setIntegration(i)}
                            />
                        ))}
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                        <thead>
                            <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                                {FAILURE_HEADERS.map((h) => (
                                    <th key={h} style={{ padding: '9px 14px', fontWeight: 600 }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td
                                    colSpan={FAILURE_HEADERS.length}
                                    style={{ padding: 30, textAlign: 'center', color: C.textMuted }}
                                >
                                    No failed transactions to show yet.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function AsnScreen() {
    const [range, setRange] = useState<ActiveRange>({ kind: 'preset', preset: '7d' });
    const [showCustom, setShowCustom] = useState(false);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [drawer, setDrawer] = useState<{
        integration: Integration | 'all';
        bucket: Bucket | 'all';
    } | null>(null);

    const isPreset = (p: Preset) => range.kind === 'preset' && range.preset === p;
    const customDays = daysBetween(from, to);
    const applyCustom = () => {
        if (customDays > 0) setRange({ kind: 'custom', from, to, days: customDays });
    };
    // No backend yet, so there are no failures to surface.
    const totalFailures = 0;

    return (
        <div style={{ fontFamily: F.ui, color: C.text }}>
            {drawer && (
                <FailuresPanel
                    range={range}
                    initIntegration={drawer.integration}
                    initBucket={drawer.bucket}
                    onClose={() => setDrawer(null)}
                />
            )}

            {/* Header: title + failures button + range presets */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 12,
                }}
            >
                <div>
                    <h1 style={{ margin: '0 0 4px', fontSize: T.title, fontWeight: 700, color: C.text }}>
                        ASN — Advance Shipping Notice
                    </h1>
                    <p style={{ margin: 0, fontSize: T.body, color: C.textSub }}>
                        See what&rsquo;s pending, upload new ASN files, and review your submission
                        summary.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                        type="button"
                        onClick={() => setDrawer({ integration: 'all', bucket: 'all' })}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            height: 38,
                            padding: '0 14px',
                            cursor: 'pointer',
                            fontFamily: F.ui,
                            fontSize: T.body,
                            fontWeight: 600,
                            color: totalFailures ? C.red : C.textSub,
                            background: C.surface,
                            border: `1px solid ${totalFailures ? '#FECACA' : C.border}`,
                            borderRadius: 8,
                        }}
                    >
                        View all failures
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 22,
                                height: 20,
                                padding: '0 6px',
                                borderRadius: 10,
                                fontSize: T.caption,
                                fontWeight: 700,
                                color: '#fff',
                                background: totalFailures ? C.red : C.textMuted,
                            }}
                        >
                            {totalFailures}
                        </span>
                    </button>
                    <div
                        style={{
                            display: 'flex',
                            gap: 4,
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                            borderRadius: 8,
                            padding: 3,
                        }}
                    >
                        {PRESETS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => {
                                    setRange({ kind: 'preset', preset: p });
                                    setShowCustom(false);
                                }}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: F.ui,
                                    fontSize: T.body,
                                    fontWeight: isPreset(p) ? 700 : 500,
                                    background: isPreset(p) ? C.primary : 'transparent',
                                    color: isPreset(p) ? '#fff' : C.textSub,
                                }}
                            >
                                {p}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setShowCustom((s) => !s)}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 6,
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: F.ui,
                                fontSize: T.body,
                                fontWeight: range.kind === 'custom' ? 700 : 500,
                                background: range.kind === 'custom' ? C.primary : 'transparent',
                                color: range.kind === 'custom' ? '#fff' : C.textSub,
                            }}
                        >
                            Custom
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom date range */}
            {showCustom && (
                <Card style={{ marginBottom: 16 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 14,
                            padding: 16,
                            flexWrap: 'wrap',
                        }}
                    >
                        <div>
                            <div style={{ fontSize: T.caption, color: C.textSub, marginBottom: 4 }}>
                                From
                            </div>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                style={{
                                    height: 36,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 6,
                                    padding: '0 10px',
                                    fontSize: T.body,
                                    fontFamily: F.ui,
                                }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: T.caption, color: C.textSub, marginBottom: 4 }}>
                                To
                            </div>
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                style={{
                                    height: 36,
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 6,
                                    padding: '0 10px',
                                    fontSize: T.body,
                                    fontFamily: F.ui,
                                }}
                            />
                        </div>
                        <Btn label="Submit" variant="pri" onClick={applyCustom} />
                        <span
                            style={{
                                fontSize: T.caption,
                                color: customDays > 0 ? C.textMuted : C.red,
                                alignSelf: 'center',
                            }}
                        >
                            {customDays > 0
                                ? `${customDays} day(s) selected`
                                : 'Select a valid range (To must be on/after From)'}
                        </span>
                    </div>
                </Card>
            )}

            {/* Integration summary cards */}
            <h2 style={{ margin: '0 0 12px', fontSize: T.section, fontWeight: 700, color: C.text }}>
                Submission summary by integration ({rangeLabel(range)})
            </h2>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 14,
                    marginBottom: 24,
                }}
            >
                {INTEGRATIONS.map((intg) => (
                    <IntegrationCard
                        key={intg}
                        intg={intg}
                        onViewFailures={(bucket) => setDrawer({ integration: intg, bucket })}
                    />
                ))}
            </div>

            {/* Pending ASNs worklist */}
            <PendingAsns />

            {/* Upload ASN file */}
            <h2 style={{ margin: '0 0 12px', fontSize: T.section, fontWeight: 700, color: C.text }}>
                Upload ASN file
            </h2>
            <UploadAsn />
        </div>
    );
}
